import type { BulkLimits, BulkRowBase, BulkStatus } from "./types";

/** Why a picked file can't be sent at all: the wrong format or over the size
 *  limit. The API refuses both too; checking here saves the upload. */
export type BulkFileProblem = "format" | "size";

export function bulkFileProblem(
  file: File,
  limits: BulkLimits,
): BulkFileProblem | null {
  if (!file.name.toLocaleLowerCase().endsWith(limits.fileExtension)) {
    return "format";
  }
  return file.size > limits.maxFileMb * 1024 * 1024 ? "size" : null;
}

/** The Check step's filter chips: every row, or one status. */
export type BulkFilter = "ALL" | BulkStatus;

export function filterBulkRows<Row extends BulkRowBase>(
  rows: readonly Row[],
  filter: BulkFilter,
): readonly Row[] {
  return filter === "ALL" ? rows : rows.filter((row) => row.status === filter);
}

const STATUS_ORDER: Record<BulkStatus, number> = {
  VALID: 0,
  INVALID: 1,
  DUPLICATE: 2,
};

/** Sorted by status (valid, invalid, duplicate, or the reverse), and by row
 *  within one. */
export function sortBulkRows<Row extends BulkRowBase>(
  rows: readonly Row[],
  direction: "asc" | "desc",
): readonly Row[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort(
    (a, b) =>
      sign * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || a.row - b.row,
  );
}

/** A cell as read from the sheet, for the table: blank when empty. */
export function bulkCell(value: number | string | null): string {
  return value === null ? "" : String(value);
}
